import { Calculator } from "lucide-react";
import { Link } from "react-router-dom";
import Brand from "../components/Brand";

/**
 * StackTheDeck — visual price comparison.
 * Adds up what each tool would cost on rival platforms, strikes through the total,
 * reveals "$49/mo" as an 85%+ savings. Pure conversion psychology.
 */
const STACK_ITEMS = [
  { tool: "Funnel Builder + 15 Templates",     compare: "vs ClickFunnels",    price: 97  },
  { tool: "AI Writer · 30 generations/day",    compare: "vs Jasper / Copy.ai", price: 20  },
  { tool: "Email Engine + Auto Follow-Up",     compare: "vs Mailchimp tier",  price: 50  },
  { tool: "Full Academy · 10+ Courses",        compare: "vs courses elsewhere", price: 25  },
  { tool: "AI Book Cover Maker",               compare: "vs Bookbrush",       price: 30  },
  { tool: "Member Directory + Your Own Page",  compare: "vs Mighty Networks", price: 20  },
  { tool: "50/50 Affiliate Engine",            compare: "vs FirstPromoter",   price: 50  },
  { tool: "Community Board + Helpdesk",        compare: "vs Circle",          price: 30  },
];

export default function StackTheDeck() {
  const total = STACK_ITEMS.reduce((s, i) => s + i.price, 0);
  const yourPrice = 49;
  const savePct = Math.round(100 - (yourPrice / total) * 100);
  const savings = total - yourPrice;
  return (
    <div className="rounded-2xl bg-white border-4 border-[#F39A1F] p-6 md:p-9 shadow-2xl" data-testid="stack-the-deck">
      <div className="text-center mb-7">
        <div className="inline-flex items-center gap-2 bg-[#F39A1F]/15 border border-[#F39A1F]/40 rounded-full px-4 py-1.5 mb-3">
          <Calculator className="w-3.5 h-3.5 text-gold-deep"/>
          <span className="text-[10px] tracking-wider-cap font-bold text-gold-deep">★ THE MATH · WHAT YOU'D PAY ELSEWHERE</span>
        </div>
        <h3 className="font-serif-display text-3xl md:text-4xl text-red-wine uppercase leading-tight text-balance">If You Built This Stack Separately…</h3>
        <p className="text-foreground/70 max-w-xl mx-auto mt-2 text-sm">Real prices from real platforms — what every ministry pays, piecemeal.</p>
      </div>

      <div className="space-y-1 max-w-2xl mx-auto" data-testid="stack-rows">
        {STACK_ITEMS.map((it, idx) => (
          <div key={idx} className="grid grid-cols-[1fr_auto] md:grid-cols-[1fr_auto_auto] items-center gap-2 md:gap-3 py-2.5 border-b border-foreground/10" data-testid={`stack-row-${idx}`}>
            <div>
              <div className="text-foreground font-bold text-sm">{it.tool}</div>
              <div className="text-foreground/55 text-[10px] tracking-wider-cap font-bold uppercase mt-0.5 md:hidden">{it.compare}</div>
            </div>
            <div className="hidden md:block text-foreground/55 text-[10px] tracking-wider-cap font-bold uppercase">{it.compare}</div>
            <div className="font-serif-display text-xl text-red-wine whitespace-nowrap">${it.price}<span className="text-xs text-foreground/55">/mo</span></div>
          </div>
        ))}

        <div className="grid grid-cols-[1fr_auto] items-center gap-3 pt-4 border-t-4 border-[#F39A1F]/40" data-testid="stack-total">
          <div className="text-foreground font-bold uppercase tracking-wider-cap text-xs md:text-sm">Total · what others charge:</div>
          <div className="font-serif-display text-3xl md:text-4xl text-foreground/40 line-through whitespace-nowrap">${total}<span className="text-sm">/mo</span></div>
        </div>

        <div className="grid grid-cols-[1fr_auto] items-center gap-3 pt-2" data-testid="stack-your-price">
          <div className="font-bold uppercase tracking-wider-cap text-xs md:text-sm">
            <span className="text-[#2E9F3A]">★ YOUR <Brand/> PRICE ★</span>
          </div>
          <div className="font-serif-display text-5xl md:text-6xl text-[#2E9F3A] whitespace-nowrap">${yourPrice}<span className="text-sm md:text-base text-foreground">/mo</span></div>
        </div>

        <div className="text-center pt-3">
          <div className="inline-block bg-[#F39A1F] text-[#0F172A] text-[11px] font-bold uppercase tracking-wider-cap px-4 py-1.5 rounded-full">
            ★ YOU SAVE {savePct}% · ${savings.toLocaleString()}/MO
          </div>
        </div>
      </div>

      <div className="text-center mt-7">
        <Link to="/signup?plan=faithful_monthly" data-testid="stack-cta" className="inline-block bg-[#2E9F3A] text-white px-7 md:px-9 py-3.5 md:py-4 rounded-md font-bold uppercase tracking-wider-cap text-sm hover:bg-[#1F8A2A] transition shadow-xl">
          Lock In $49/mo · 30-Day Money Back →
        </Link>
        <div className="mt-2 text-[10px] tracking-wider-cap font-bold text-foreground/55">★ NO CONTRACT · CANCEL ANYTIME ★</div>
      </div>
    </div>
  );
}
